import { Container, Modal, TabItemProps, Tabs } from '@/components';
import { queryClient } from '@/constants/query-client';
import { BusinessAreasKey } from '@/modules/BusinessAreas/queries/keys';
import { CompanyKeys } from '@/modules/Companies/queries';
import { OrganizationKeys } from '@/modules/Organizations/queries/keys';
import { TaskKey } from '@/modules/Tasks/queries';
import { useCreateUser } from '@/modules/Users/queries/create-user';
import { UserKey } from '@/modules/Users/queries/types';
import { UserSchemaType } from '@/modules/Users/types';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCsvTabModal } from '../AddCsvTab';
import { UserModal } from '../UserModal';

interface CreateUserModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

export const CreateUserModal = ({
  isOpen,
  handleClose,
}: CreateUserModalProps): ReactElement => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { mutate: createUser, isLoading } = useCreateUser();

  const handleConfirm = useCallback(
    (data: UserSchemaType) => {
      createUser(
        {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          status: data.status,
          access: data.access,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(UserKey.ALL);
            queryClient.invalidateQueries(OrganizationKeys.ALL);
            queryClient.invalidateQueries(CompanyKeys.ALL);
            queryClient.invalidateQueries(BusinessAreasKey.ALL);
            queryClient.invalidateQueries(TaskKey.ALL);
            handleClose();
          },
        },
      );
    },
    [createUser, handleClose],
  );

  const tabs: Array<TabItemProps> = useMemo(
    () => [
      { label: t('create_new'), badgeNumber: 0 },
      {
        label: t('add_through_csv'),
        badgeNumber: 0,
      },
    ],
    [t],
  );

  const onTabChange = useCallback(
    (tab: number) => {
      setSelectedTab(tab);
    },
    [setSelectedTab],
  );

  const tabList = useMemo(() => {
    return [
      {
        index: 0,
        tab: (
          <UserModal
            handleOnSubmit={handleConfirm}
            handleClose={handleClose}
            isLoading={isLoading}
            type="create"
          />
        ),
      },
      {
        index: 1,
        tab: <AddCsvTabModal handleClose={handleClose} />,
      },
    ];
  }, [handleConfirm, handleClose, isLoading]);

  const elementToRender = tabList.find((el) => el.index === selectedTab);
  const modalTitle = selectedTab !== 2 ? 'create_users' : 'add_users';

  return (
    <Modal
      isOpen={isOpen}
      title={t(modalTitle)}
      className="h-fit w-128"
      handleClose={handleClose}
    >
      <div className="mt-6 flex flex-col">
        <Container>
          <Tabs activeTab={selectedTab} tabs={tabs} onTabChange={onTabChange} />
        </Container>
        <div className="flex flex-col gap-2">{elementToRender?.tab}</div>
      </div>
    </Modal>
  );
};
